import { inject, Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TELLENT_CURRENT_USER } from '@tellent/types';
import { map, of } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';

import { createEffectFactory } from '@recruitee/ngrx';

import { TellentMembersHttpClient } from './tellent-members.http';
import { TellentMembersActions } from './tellent-members.store';

@Injectable({ providedIn: 'root' })
export class TellentMembersEffects {
  public effects = createEffectFactory(TellentMembersActions, this.actions$);

  public inviteMember$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentMembersActions.inviteMember),
      mergeMap(({ payload }) =>
        this.tellentMembersHttpClient.inviteMember(payload).pipe(
          map(data => TellentMembersActions.inviteMemberSuccess({ payload: data })),
          catchError(error => of(TellentMembersActions.inviteMemberFail({ payload: error }))),
        ),
      ),
    ),
  );

  public fetchMembers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentMembersActions.fetchPaginatedCollection),
      mergeMap(({ payload }) =>
        this.tellentMembersHttpClient.fetchMembers(payload.params).pipe(
          map(({ data, meta }) =>
            TellentMembersActions.fetchPaginatedCollectionSuccess({
              payload: { data: { rows: data, meta }, scope: payload.params.scope },
            }),
          ),
          catchError(error =>
            of(TellentMembersActions.fetchPaginatedCollectionFailed({ payload: error })),
          ),
        ),
      ),
    ),
  );

  public deactivateMember$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentMembersActions.deactivateMember),
      mergeMap(({ payload }) =>
        this.tellentMembersHttpClient.deactivateMember(payload).pipe(
          map(response => TellentMembersActions.deactivateMemberSuccess({ payload: response })),
          catchError(error => of(TellentMembersActions.deactivateMemberFailed({ payload: error }))),
        ),
      ),
    ),
  );

  public reactivateMember$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentMembersActions.reactivateMember),
      mergeMap(({ payload }) =>
        this.tellentMembersHttpClient.reactivateMember(payload).pipe(
          map(response => TellentMembersActions.reactivateMemberSuccess({ payload: response })),
          catchError(error => of(TellentMembersActions.reactivateMemberFailed({ payload: error }))),
        ),
      ),
    ),
  );

  public sendInvitation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentMembersActions.sendInvitation),
      mergeMap(({ payload }) =>
        this.tellentMembersHttpClient.sendInvitation(payload).pipe(
          map(() => TellentMembersActions.sendInvitationSuccess({ payload })),
          catchError(error => of(TellentMembersActions.sendInvitationFailed({ payload: error }))),
        ),
      ),
    ),
  );

  public resendInvitation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentMembersActions.resendInvitation),
      mergeMap(({ payload }) =>
        this.tellentMembersHttpClient.resendInvitation(payload).pipe(
          map(() => TellentMembersActions.resendInvitationSuccess({ payload })),
          catchError(error => of(TellentMembersActions.resendInvitationFailed({ payload: error }))),
        ),
      ),
    ),
  );

  constructor(
    private tellentMembersHttpClient: TellentMembersHttpClient,
    private actions$: Actions,
  ) {}

  public ngrxOnInitEffects() {
    return TellentMembersActions.fetchCollectionSuccess({
      payload: { data: inject(TELLENT_CURRENT_USER).members || [] },
    });
  }
}
